import { type ReactNode, Suspense, lazy } from "react";
import { Sparkles } from "lucide-react";
import { SidebarMenu, SidebarMenuButton, SidebarMenuItem, useSidebar } from "@/components/ui/sidebar";
import { cn } from "@/lib/utils";
import { useNeueAnsicht } from "./useNeueAnsicht";

// Die Hülle wird erst geladen, wenn sie gebraucht wird: Mitglieder laden davon
// keine einzige Datei (auch nicht labor-design.css).
const NeueAnsichtHuelle = lazy(() => import("./NeueAnsichtHuelle"));

/**
 * Ladefläche in der Form der Hülle, solange sie nachlädt (Sitzung A2: Ladeflächen statt Kreisel). Farben fest, weil
 * `labor-design.css` erst mit der Hülle kommt.
 */
function HuelleLaedt() {
  const flaeche = "rounded-2xl bg-[#F2F0EA] motion-safe:animate-pulse";
  return (
    <div className="flex min-h-screen w-full bg-[#FAF9F5]" aria-busy="true" aria-label="Wird geladen">
      <div className="hidden w-[284px] shrink-0 bg-white md:block" />
      <div className="flex min-w-0 flex-1 flex-col">
        <div className="h-[60px] bg-white md:hidden" />
        <div className="flex flex-col gap-4 px-4 pt-4 md:px-8 md:pt-8 lg:px-12">
          <div className={cn(flaeche, "h-8 w-2/3 max-w-sm")} />
          <div className={cn(flaeche, "h-40")} />
          <div className={cn(flaeche, "h-24")} />
        </div>
      </div>
      <div className="fixed bottom-0 left-0 right-0 h-[78px] bg-white shadow-[0_-1px_0_#e4e9e8] md:hidden" />
    </div>
  );
}

/**
 * Die neue Hülle um eine Seite des Mitgliederbereichs. `StudentLayout` setzt
 * sie nur ein, wenn `useNeueAnsicht().aktiv` gilt (Etappe 8, 4a).
 */
export function NeueAnsicht({ children }: { children: ReactNode }) {
  return (
    <Suspense fallback={<HuelleLaedt />}>
      <NeueAnsichtHuelle>{children}</NeueAnsichtHuelle>
    </Suspense>
  );
}

/**
 * Knopf „Neue Ansicht“ in der gewohnten Seitenleiste, im Stil der Einträge
 * darüber. Erscheint nur, wenn der Server den Schalter für dieses Konto
 * freigibt; für alle anderen rendert er nichts.
 */
export function NeueAnsichtKnopf({ kontoId }: { kontoId: string | null | undefined }) {
  const { darf, aktiv, waehlen } = useNeueAnsicht(kontoId);
  const { state } = useSidebar();
  if (!darf || aktiv) return null;
  const eng = state === "collapsed";

  return (
    <SidebarMenu className={cn("mt-1 space-y-0.5", !eng && "sidebar-section-card")}>
      <SidebarMenuItem>
        <SidebarMenuButton asChild tooltip="Neue Ansicht" className="group/neu rounded-xl hover:bg-primary/5">
          <button
            type="button"
            onClick={() => waehlen("neu")}
            className={cn("flex w-full min-w-0 items-center", eng ? "justify-center" : "gap-2 px-3 py-2")}
          >
            <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-primary/[0.08] text-primary/70 ring-1 ring-primary/10 transition-all duration-200 group-hover/neu:bg-primary/15 group-hover/neu:text-primary">
              <Sparkles className="h-4 w-4" />
            </span>
            {!eng && <span className="truncate font-medium">Neue Ansicht</span>}
          </button>
        </SidebarMenuButton>
      </SidebarMenuItem>
    </SidebarMenu>
  );
}
